import { useEffect, useMemo, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { CopyIcon, DownloadIcon, LockIcon } from "lucide-react";
import QRCodeStyling from "qr-code-styling";

import { Button } from "simplydo/react/ui/button";
import { H3, H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import AppIcon from "src/images/appIcon.png";

import { SuggestedMFAApps } from "components/lib/Mfa/MFAAppLinks";

export const EnableMfa = ({
  open,
  onClose,
  onEnableMfa,
}: {
  open: boolean;
  onClose: () => void;
  onEnableMfa: () => void;
}) => {
  const { t } = useTranslation();
  const [otpVerifier, setOtpVerifier] = useState("");
  const otpQrRef = useRef<HTMLDivElement | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [uri, setUri] = useState<string>();
  const [recoveryCodesModalOpen, setRecoveryCodesModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      api.auth.generateOtpUri(
        (check) => {
          setUri(check.uri);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    }
  }, [open]);

  const qrCode = useMemo(
    () =>
      new QRCodeStyling({
        width: 200,
        height: 200,
        type: "svg",
        data: uri,
        image: AppIcon,
        dotsOptions: { color: "#14435B" },
        backgroundOptions: { color: "#ffffff" },
        imageOptions: { margin: 4 },
      }),
    [uri],
  );

  useEffect(() => {
    if (open && !qrCode._container) {
      qrCode.append(otpQrRef.current ?? undefined);
    }
  }, [open, qrCode]);

  const downloadCodes = () => {
    const codes = recoveryCodes.join(", ");
    const blob = new Blob([codes], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mfa-recovery-codes.txt";
    a.click();
  };

  const copyCodes = () => {
    const codes = recoveryCodes.join(", ");
    navigator.clipboard.writeText(codes);
    toast.success(t("entry.mfa.codesCopied"));
  };

  const checkMfaCode = () => {
    const loadingId = toast.loading(t("entry.mfa.statusMessageLoading"));
    setMfaLoading(true);
    api.auth.enableOtp(
      { otp: otpVerifier },
      (response) => {
        setMfaLoading(false);
        // Don't reload user yet — under MFA enforcement that unmounts this
        // tree and closes the recovery codes modal before codes can be saved.
        onClose();
        setRecoveryCodes(
          (response as { recoveryCodes?: string[] }).recoveryCodes ?? [],
        );
        setOtpVerifier("");
        toast.success(t("entry.mfa.statusMessageSuccess"), { id: loadingId });
        setRecoveryCodesModalOpen(true);
      },
      (err) => {
        setMfaLoading(false);
        toast.error(err.message, { id: loadingId });
        setOtpVerifier("");
      },
    );
  };

  const dismissRecoveryCodes = () => {
    setRecoveryCodesModalOpen(false);
    onEnableMfa?.();
  };

  return (
    <>
      <Modal open={open}>
        <ModalHeader>
          <Icon icon={LockIcon} style={{ paddingRight: "5px" }} />
          {t("entry.mfa.enableAction")}
        </ModalHeader>
        <ModalContent scrolling>
          <H3>{t("generic.step")} 1</H3>
          <p>{t("entry.mfa.step1")}</p>
          <Message color="blue">
            <SuggestedMFAApps />
          </Message>
          <H3>{t("generic.step")} 2</H3>
          <p>{t("entry.mfa.step2")}</p>
          <div
            style={{ width: "70%", margin: "10px auto", textAlign: "center" }}
            ref={otpQrRef}
          />
          <H3>{t("generic.step")} 3</H3>
          <p>{t("entry.mfa.step3")}</p>
          <div style={{ textAlign: "center" }}>
            <Input
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.keyCode === 13) {
                  checkMfaCode();
                }
              }}
              size="large"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOtpVerifier(e.target.value)
              }
              value={otpVerifier}
              placeholder="123456"
              autoFocus
            />
          </div>
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => {
              onClose();
            }}
          >
            {t("generic.cancel")}
          </Button>
          <Button
            loading={mfaLoading}
            onClick={() => checkMfaCode()}
            disabled={otpVerifier.replaceAll(" ", "").length !== 6}
            color="primary"
          >
            {t("generic.verify")}
          </Button>
        </ModalActions>
      </Modal>
      <Modal open={recoveryCodesModalOpen}>
        <ModalHeader>{t("entry.mfa.recoveryTitle")}</ModalHeader>
        <ModalContent>
          <p>{t("entry.mfa.recoverySetupInfo")}</p>
          <p>{t("entry.mfa.recoverySetupInfoImportant")}</p>
          <H5>{t("entry.mfa.yourRecoveryCodes")}</H5>
          <div
            style={{
              marginBottom: "20px",
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {recoveryCodes.map((code, index) => (
              <Label key={index}>{code}</Label>
            ))}
          </div>
          <Button size="small" onClick={() => downloadCodes()}>
            <Icon icon={DownloadIcon} />
            {t("entry.mfa.downloadCodes")}
          </Button>
          <Button size="small" onClick={() => copyCodes()}>
            <Icon icon={CopyIcon} />
            {t("entry.mfa.copyCodes")}
          </Button>
        </ModalContent>
        <ModalActions>
          <Button onClick={dismissRecoveryCodes} color="primary">
            {t("entry.mfa.codesSaved")}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};
